import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * Nora chat service layer — GMA-1520.
 *
 * Lets a caregiver ask Nora about a specific client from the web client
 * profile. The backend (GMA-1519, POST /api/v3/ai/chat) validates the
 * caregiver's access to the client and grounds Nora's reply in that client's
 * Pontosense / Silver Shield data.
 *
 * `apiRequest` returns the raw fetch Response, so we parse + throw via the
 * shared parseJsonOrThrow helper.
 *
 * @param {object}   p
 * @param {number}   p.clientId        the client the caregiver is asking about (required)
 * @param {string}   p.message         the caregiver's message (required, non-empty)
 * @param {Array}    [p.history]       prior [{ role, content }] turns
 * @param {string}   [p.clientName]    client's display name (for the briefing)
 * @param {string}   [p.caregiverName] caregiver's display name
 * @returns {Promise<{reply:string, conversationId:string, risk:object, actions:string[]}>}
 */
export const sendNoraMessage = async ({
  clientId,
  message,
  history = [],
  clientName,
  caregiverName,
}) => {
  if (clientId === null || clientId === undefined) {
    throw new Error("sendNoraMessage requires a clientId");
  }
  if (!message || !message.trim()) {
    throw new Error("sendNoraMessage requires a non-empty message");
  }

  const context = { clientId };
  if (clientName) context.clientName = clientName;
  if (caregiverName) context.caregiverName = caregiverName;

  const response = await apiRequest("ai/chat", {
    method: "POST",
    version: "v3",
    body: { message, history, context },
  });
  return parseJsonOrThrow(response, "Nora chat");
};
